<template>
  <div class="ctf-video" data-component="video">
    <figure v-if="videoUrl" :class="['ciwt-video', { 'img-cover': !isEmbed }]">
      <video
        v-if="!isEmbed"
        ref="video"
        :alt="altText"
        controls
        :poster="videoThumbnail"
        :preload="videoLazyLoad ? 'auto' : 'none'"
        @play="playerState = PLAYER_STATE.playing"
        @pause="playerState = PLAYER_STATE.paused"
        @ended="playerState = PLAYER_STATE.ended"
      >
        <source
          :src="videoThumbnail ? videoUrl : `${videoUrl}#t=0.1`"
          type="video/mp4"
        />
      </video>

      <span
        class="play-video-btn centered"
        v-if="!isEmbed && (idle || ended)"
        @click="playVideo"
        @keydown.enter="playVideo"
        @keydown.space="playVideo"
        role="button"
        aria-label="Play video"
        tabindex="0"
      />

      <iframe
        v-if="isEmbed"
        :src="videoUrl"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowfullscreen
      />
    </figure>
  </div>
</template>

<script setup lang="ts">
const PLAYER_STATE = {
  idle: "idle",
  playing: "playing",
  paused: "paused",
  ended: "ended",
} as const;

type PlayerState = (typeof PLAYER_STATE)[keyof typeof PLAYER_STATE];

const props = defineProps({
  source: {
    type: String, // DAM, Vimeo or Youtube
    required: true,
  },
  asset: Object, // Bynder DAM
  videoId: String, // Video & Youtube
  videoHash: String,
  altText: {
    type: String,
    required: true,
  },
  customThumbnail: Object,
  videoLazyLoad: {
    type: Boolean,
    default: true,
  },
  disabled: Boolean,
});

const video = ref();
const playerState = ref<PlayerState>(PLAYER_STATE.idle);

const idle = computed(() => playerState.value === PLAYER_STATE.idle);
const ended = computed(() => playerState.value === PLAYER_STATE.ended);

const isEmbed = ref(
  ["Vimeo", "Youtube"].some((source) => props.source === source),
);

const videoThumbnail = computed<string>(() => {
  if (isEmbed.value) return;

  return props.customThumbnail || props.asset?.[0]?.thumbnails?.webimage;
});

function playVideo() {
  if (!props.disabled) {
    video.value?.play();
  }
}

const videoUrl = computed(() => {
  switch (props.source) {
    case "Vimeo":
      return (
        props.videoId &&
        `https://player.vimeo.com/video/${props.videoId}?h=${props.videoHash}`
      );
    case "Youtube":
      return (
        props.videoId && `https://www.youtube.com/embed/${props.videoId}?rel=0`
      );
    case "DAM": {
      return props.asset?.[0].videoPreviewURLs[0];
    }
    default:
      return;
  }
});
</script>

<style lang="scss">
.ctf-video {
  iframe {
    width: 100%;
    aspect-ratio: 16/9;
  }

  figure {
    margin: 0;
    line-height: 0;
  }
}
</style>
